import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { HelpCircle, X, Send, Loader2 } from "lucide-react";
import ReactMarkdown from "react-markdown";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HelpAgent() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([
    { role: "assistant", content: "Hi! I'm your **ElastoAI Help Agent**. Ask me how to use any feature — Chat, Formulation, Documents, Training, etc." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [msgs, open]);

  const send = async () => {
    const q = input.trim();
    if (!q || loading) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", content: q }]);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/help`, { message: q });
      setMsgs((m) => [...m, { role: "assistant", content: res.data.response }]);
    } catch (e) {
      setMsgs((m) => [...m, { role: "assistant", content: "Sorry, I couldn't reach the help service." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="help-agent-toggle"
          className="fixed bottom-6 right-6 z-50 bg-orange-500 hover:bg-orange-600 text-white rounded-full p-4 shadow-2xl hover:shadow-orange-500/40 hover:-translate-y-1 transition-all"
          title="Help Agent"
        >
          <HelpCircle className="w-6 h-6" />
        </button>
      )}

      {open && (
        <div className="fixed bottom-6 right-6 z-50 w-[380px] max-w-[92vw] h-[520px] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden" data-testid="help-agent-panel">
          <div className="flex items-center justify-between p-4 bg-slate-900 text-white">
            <div className="flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-orange-400" />
              <span className="font-heading font-semibold">Help Agent</span>
            </div>
            <button onClick={() => setOpen(false)} data-testid="help-agent-close" className="hover:bg-slate-800 rounded p-1">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3 py-2 rounded-lg text-sm ${m.role === "user" ? "bg-slate-900 text-white" : "bg-white text-slate-800 border border-slate-200"}`}>
                  <div className="prose prose-sm max-w-none">
                    <ReactMarkdown>{m.content}</ReactMarkdown>
                  </div>
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-slate-200 px-3 py-2 rounded-lg">
                  <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
                </div>
              </div>
            )}
          </div>

          <div className="p-3 border-t border-slate-200 bg-white">
            <div className="flex gap-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="How do I export a formulation?"
                rows={1}
                data-testid="help-input"
                className="flex-1 resize-none border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
              />
              <button onClick={send} disabled={loading || !input.trim()} data-testid="help-send" className="bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white rounded-lg px-3">
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
